// R115 maint — B6 "click a date → choose what to schedule", the work-order
// branch: the chooser's "Schedule a work order" (inspector 'date' action 'wo')
// opens this form sheet for ONE home + ONE day. It creates the WO already
// booked for that day through the Maintenance module's own create route
// (POST /api/assetops/work-orders, source 'calendar', created_by = you), so
// the assignee DM, the owner-handled gate and the WO chain all apply. A taken
// night is allowed (DECISIONS: WO work during a stay is legitimate) with a
// guest-in-house warning and the guest-permission switch, which the create
// route stores in the same single write. Past days are refused.
// R115 integ fix (D2): opened from the toolbar "+ Add" there is no home yet —
// the sheet asks "Which home?" inline (../homePick) and redraws the home line,
// the guest-night warning and the permission switch for the home picked.
import { registerAction, type CalendarCtx, type DateCell } from '../registry';
import { homePickHtml, onHomePick } from '../homePick';
import { vendorOptionsHtml, rosterDatalistHtml, WO_PRIORITY_OPTS, WO_ROLE_OPTS } from '../../../shared/components/WoEditor';
import { api, WO_API, LAYER_ID, OFFICE, MAINT_VIEWS, loadVendors, loadRoster, nightGuest, nightWarn, wmd, md } from './shared';

// R115 review: busy / dirty belong to ONE sheet — a late answer for a sheet the user already closed never touches
// (or force-closes) the sheet open now
interface NewWoSheet { busy: boolean; dirty: boolean; closed: boolean }
let cur: NewWoSheet | null = null;
export const newWoBusy = (): boolean => !!cur && !cur.closed && (cur.busy || cur.dirty);

export function openNewWoSheet(ctx: CalendarCtx, listingId: string, date: string): void {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ''))) { ctx.toast('Pick a day first.'); return; }
  if (date < ctx.asOf) { ctx.toast("That day has passed — a work order can be booked from today on."); return; }
  let lid = String(listingId || '');
  const e = ctx.esc;
  const S: NewWoSheet = { busy: false, dirty: false, closed: false };
  cur = S;
  // one id per sheet: a retry after a dropped connection is the SAME work order on the server
  const clientRef = (() => { try { return crypto.randomUUID(); } catch { return `wo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`; } })();
  const names = (id: string) => { const p = ctx.property(id); return { home: (p && (p.full_name || p.name)) || id, short: (p && (p.name || p.full_name)) || id }; };
  // the home line, the guest-night warning and the permission switch all follow the home (picked inline when there was none)
  const homeHtml = (id: string): string => {
    if (!id) return `<div class="mw-sheet-sub">Pick the home below · <b>${e(wmd(date))}</b></div>`;
    const { home, short } = names(id);
    const g = nightGuest(ctx, id, date);
    const turnDay = !g && ctx.staysOf(id).some(s => s && s.kind !== 'inquiry' && s.check_out === date);
    return `<div class="mw-sheet-sub"><b>${e(short)}</b>${home !== short ? ` <span class="mw-muted">${e(home)}</span>` : ''} · <b>${e(wmd(date))}</b></div>
      ${g ? `<div class="mw-warn" data-mw-guestwarn>${nightWarn(g, wmd(date), e)}</div>` : ''}
      ${turnDay ? '<div class="mw-ok">🧳 Checkout day — a good day to get in (after the clean).</div>' : ''}`;
  };
  const gpHtml = (id: string): string => {
    const g = id ? nightGuest(ctx, id, date) : null;
    return g ? `<label class="mw-check"><input type="checkbox" name="gp"> 🚪 ${g.kind === 'owner' ? 'Owner' : 'Guest'} gave permission to enter</label>
      <label class="mw-field">Access mode
        <select class="mw-input" name="gp_mode" disabled><option value="guest_present">${g.kind === 'owner' ? 'owner' : 'guest'} will be present</option><option value="own_code">enter with own code</option></select>
      </label>` : '';
  };
  const sheet = ctx.openSheet(`
    <form class="mw mw-sheet mw-new" data-mw-new novalidate>
      <div class="mw-block" data-mw-home>${homeHtml(lid)}</div>
      ${lid ? '' : homePickHtml(ctx, { field: 'mw-field', input: 'mw-input' })}
      <label class="mw-field">What needs doing?
        <input class="mw-input" name="title" maxlength="200" required autocomplete="off" placeholder="e.g. Fix the sticky gate latch">
      </label>
      <div class="mw-grid2">
        <label class="mw-field">Priority
          <select class="mw-input" name="priority">${WO_PRIORITY_OPTS.filter(([k]) => k !== 'pm').map(([k, l]) => `<option value="${k}" ${k === 'routine' ? 'selected' : ''}>${l}</option>`).join('')}</select>
        </label>
        <label class="mw-field">Route to
          <select class="mw-input" name="role">${WO_ROLE_OPTS.map(([k, l]) => `<option value="${k}">${l}</option>`).join('')}</select>
        </label>
      </div>
      <label class="mw-field">Assigned to
        <input class="mw-input" name="assigned_to" list="mw-new-roster" autocomplete="off" placeholder="teammate or vendor — or type a name">
      </label>
      <span data-mw-roster-host style="display:none"></span>
      <div class="mw-grid2">
        <label class="mw-field">Vendor
          <select class="mw-input" name="vendor_id" data-mw-vendor-sel><option value="">— none —</option></select>
        </label>
        <label class="mw-field"><span>Due date <span class="mw-muted">(optional)</span></span>
          <input class="mw-input" type="date" name="due_date" min="${e(ctx.asOf)}">
        </label>
      </div>
      <label class="mw-field">Instructions
        <textarea class="mw-input" name="description" rows="3" placeholder="What needs to get done, where, what you need after…"></textarea>
      </label>
      <div class="mw-block" data-mw-gp>${gpHtml(lid)}</div>
      <div class="mw-err" data-mw-err role="alert" hidden></div>
      <div class="mw-sheet-foot" data-bottom-bar>
        <button type="button" class="btn btn-secondary" data-mw-cancel>Cancel</button>
        <button type="submit" class="btn btn-primary" data-mw-create>Create &amp; book ${e(md(date))}</button>
      </div>
    </form>`, { title: 'New work order', onClose: () => { S.closed = true; S.busy = false; S.dirty = false; ctx.markDirty(false); } });

  const form = sheet.querySelector<HTMLFormElement>('[data-mw-new]');
  if (!form) return;
  const f = (n: string) => form.elements.namedItem(n) as (HTMLInputElement & HTMLSelectElement & HTMLTextAreaElement) | null;
  const errEl = form.querySelector<HTMLElement>('[data-mw-err]');
  const create = form.querySelector<HTMLButtonElement>('[data-mw-create]');
  const showErr = (html: string) => { if (errEl) { errEl.hidden = !html; errEl.innerHTML = html; } };
  // pickers fill in when the (cached) lists land — the sheet never waits on them
  void Promise.all([loadVendors(), loadRoster()]).then(([vendors, people]) => {
    if (!form.isConnected) return;
    const sel = form.querySelector<HTMLSelectElement>('[data-mw-vendor-sel]');
    if (sel) { const cur = sel.value; sel.innerHTML = vendorOptionsHtml(vendors, cur || null); }
    const host = form.querySelector('[data-mw-roster-host]');
    if (host) host.innerHTML = rosterDatalistHtml('mw-new-roster', people, vendors);
  });
  form.addEventListener('input', () => { S.dirty = true; ctx.markDirty(true); });
  const bindGp = () => { const gp = f('gp'), mode = f('gp_mode'); gp?.addEventListener('change', () => { if (mode) mode.disabled = !gp.checked; }); };
  bindGp();
  onHomePick(form, (v) => {
    lid = v;
    const h = form.querySelector<HTMLElement>('[data-mw-home]'); if (h) h.innerHTML = homeHtml(lid);
    const gpBox = form.querySelector<HTMLElement>('[data-mw-gp]'); if (gpBox) { gpBox.innerHTML = gpHtml(lid); bindGp(); }
    showErr('');
  });
  form.querySelector('[data-mw-cancel]')?.addEventListener('click', () => { S.busy = false; S.dirty = false; ctx.markDirty(false); ctx.closeSheet(); });
  setTimeout(() => { try { f('title')?.focus(); } catch { /* sheet animating */ } }, 30);

  const submit = async (override: boolean) => {
    if (S.busy || S.closed) return;
    const title = String(f('title')?.value || '').trim();
    if (!lid) { showErr('Pick the home first — which home is this work order for?'); form.querySelector<HTMLSelectElement>('[data-stc-home-pick]')?.focus(); return; }
    if (!title) { showErr('Give the work order a title — what needs doing?'); f('title')?.focus(); return; }
    const listingId = lid;
    const gp = f('gp'), mode = f('gp_mode');
    // R115 review: no listing_name — the create route uses the home's internal name like every other create path
    // (the marketing title split the home on the Maintenance board and in the techs' Slack DMs)
    const body: Record<string, unknown> = {
      listing_id: listingId, title, client_ref: clientRef,
      priority: f('priority')?.value || 'routine', role: f('role')?.value || 'maintenance',
      assigned_to: String(f('assigned_to')?.value || '').trim() || undefined,
      vendor_id: f('vendor_id')?.value || undefined,
      due_date: f('due_date')?.value || undefined,
      description: String(f('description')?.value || '').trim(),
      scheduled_date: date, source: 'calendar', by: ctx.actorName(), created_by: ctx.actorName(),
    };
    if (gp && gp.checked) body.guest_permission = { granted: true, mode: mode && mode.value === 'own_code' ? 'own_code' : 'guest_present' };
    if (override) body.owner_handled_override = true;
    S.busy = true; showErr('');
    if (create) { create.disabled = true; create.textContent = 'Creating…'; }
    const r = await api('POST', WO_API, body);
    S.busy = false;
    if (S.closed) {   // the sheet was closed meanwhile: say what happened, never close the sheet open now
      if (r.ok) { ctx.toast(`Booked “${title}” for ${wmd(date)}`); try { await ctx.reload({ base: true, layers: [LAYER_ID] }); } catch { /* page error */ } }
      return;
    }
    if (create) { create.disabled = false; create.innerHTML = `Create &amp; book ${e(md(date))}`; }
    if (!r.ok && !r.status) { showErr("The connection dropped — the work order may already be created. Press Create again: it won't make a second one."); return; }
    if (!r.ok) {
      if (r.status === 400 && r.json && r.json.error === 'owner_handled') {
        showErr(`${e(r.json.message || 'The owner handles maintenance at this home — work orders are turned off here.')} <button type="button" class="btn btn-secondary btn-sm" data-mw-override>Create anyway (the owner asked us to step in)</button>`);
        form.querySelector('[data-mw-override]')?.addEventListener('click', (ev) => { (ev.currentTarget as HTMLButtonElement).disabled = true; void submit(true); }, { once: true });
        return;
      }
      showErr(e(r.error));
      return;
    }
    const wo = r.json && r.json.work_order;
    S.dirty = false; ctx.markDirty(false);
    ctx.closeSheet();
    ctx.toast(`Booked “${title}” for ${wmd(date)}${body.assigned_to ? ` — ${String(body.assigned_to)} gets a heads-up` : ''}`);
    try { await ctx.reload({ base: true, layers: [LAYER_ID] }); } catch { /* the page shows its own load error */ }
    if (wo && wo.id) ctx.openInspector({ kind: 'wo', id: String(wo.id), listing_id: listingId, date });
  };
  form.addEventListener('submit', (ev) => { ev.preventDefault(); if (!S.busy) void submit(false); });
}

// The chooser entry (inspector 'date' = the create chooser for one listing + day).
// R115 integ fix (D2): offered with no home too (the toolbar "+ Add") — the sheet asks "Which home?" itself.
registerAction({
  key: 'wo', inspector: 'date', label: 'Schedule a work order', icon: '🔧', order: 20, roles: OFFICE, primaryIn: MAINT_VIEWS,
  when: (ref, data, ctx) => { const c = data as DateCell | null; const d = String((c && c.date) || ref.date || ''); return /^\d{4}-\d{2}-\d{2}$/.test(d) && !(c && c.past) && d >= ctx.asOf; },
  run: (ref, data, ctx) => { const c = data as DateCell | null; openNewWoSheet(ctx, String((c && c.listing_id) || ref.listing_id || ''), String((c && c.date) || ref.date || '')); },
});
